import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useToast, Modal, ConfirmModal, StatusBadge, formatDate, useIsAdmin } from '../components/shared';

function DeviceForm({ initial = {}, onSave, onClose, clients = [] }) {
  const [form, setForm] = useState({ name: '', serial: '', alias: '', notes: '', client_id: '', ...initial });
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function submit() {
    if (!form.name || !form.serial) return toast('Name and serial required', 'error');
    setLoading(true);
    try {
      await onSave(form);
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal title={initial.id ? 'Edit Device' : 'New Device'} onClose={onClose} footer={
      <>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={submit} disabled={loading}>
          {loading ? 'Saving...' : 'Save'}
        </button>
      </>
    }>
      <div className="grid-2">
        <div className="field">
          <label>Device Name *</label>
          <input className="input" value={form.name} onChange={set('name')} placeholder="e.g. Ultrasound Unit" />
        </div>
        <div className="field">
          <label>Serial Number *</label>
          <input className="input" value={form.serial} onChange={set('serial')} placeholder="SN-00123" />
        </div>
      </div>
      <div className="field">
        <label>Client / Beneficiary</label>
        <select className="select" value={form.client_id || ''} onChange={set('client_id')}>
          <option value="">— No client —</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name} ({c.type})</option>)}
        </select>
      </div>
      <div className="field">
        <label>Alias / Nickname</label>
        <input className="input" value={form.alias} onChange={set('alias')} placeholder="e.g. Blue Ultrasound" />
      </div>
      <div className="field">
        <label>Notes</label>
        <textarea className="input" value={form.notes} onChange={set('notes')} rows={2} placeholder="Any relevant info..." />
      </div>
    </Modal>
  );
}

export default function Devices() {
  const [devices, setDevices] = useState([]);
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const toast = useToast();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();

  const load = useCallback(() => {
    const params = {};
    if (search) params.search = search;
    if (clientFilter) params.client_id = clientFilter;
    api.getDevices(search, clientFilter).then(setDevices).catch(() => {});
  }, [search, clientFilter]);

  useEffect(() => {
    load();
    api.getClients().then(setClients).catch(() => {});
  }, [load]);

  async function handleSave(form) {
    if (modal?.id) {
      await api.updateDevice(modal.id, form);
      toast('Device updated');
    } else {
      await api.createDevice(form);
      toast('Device added');
    }
    setModal(null);
    load();
  }

  async function handleDelete(id) {
    await api.deleteDevice(id).catch(() => {});
    toast('Device deleted');
    setConfirm(null);
    load();
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">DEVICES <span>_</span></h1>
        {isAdmin && <button className="btn btn-primary" onClick={() => setModal('new')}>+ New Device</button>}
      </div>

      {/* Filters */}
      <div style={{ marginBottom: 16 }}>
        {clients.length > 0 && (
          <>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Client</div>
            <div className="flex gap-8" style={{ flexWrap: 'wrap', marginBottom: 12 }}>
              {clients.map(c => {
                const active = clientFilter === c.id;
                return (
                  <button key={c.id}
                    onClick={() => setClientFilter(active ? '' : c.id)}
                    style={{
                      padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                      cursor: 'pointer', border: `2px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
                      background: active ? 'var(--primary-dim)' : 'transparent',
                      color: active ? 'var(--primary)' : 'var(--text3)',
                      transition: 'all 0.15s',
                    }}>
                    {active ? '✓ ' : ''}{c.name}
                  </button>
                );
              })}
              {clientFilter && <button className="btn btn-ghost btn-sm" onClick={() => setClientFilter('')}>✕ Clear</button>}
            </div>
          </>
        )}
        <div className="search-wrap" style={{ maxWidth: 360 }}>
          <input className="input" placeholder="Search by name, serial, alias..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Client</th>
              <th>Serial</th>
              <th>Alias</th>
              <th>Tickets</th>
              <th>Last Status</th>
              <th>Added</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {devices.length === 0 ? (
              <tr><td colSpan={8}><div className="empty">No devices found</div></td></tr>
            ) : devices.map(d => (
              <tr key={d.id}>
                <td className="fw-500 link" onClick={() => navigate(`/devices/${d.id}`)}>{d.name}</td>
                <td className="text-sm">{d.client_name ? <span className="badge badge-blue">{d.client_name}</span> : <span className="text-dim">—</span>}</td>
                <td><span className="mono">{d.serial}</span></td>
                <td className="text-muted text-sm">{d.alias || '—'}</td>
                <td><span className="mono text-sm">{d.ticket_count}</span></td>
                <td>{d.last_status ? <StatusBadge status={d.last_status} /> : <span className="text-dim text-sm">—</span>}</td>
                <td className="mono text-sm text-dim">{formatDate(d.created_at)}</td>
                <td>
                  {isAdmin && (
                    <div className="flex gap-8">
                      <button className="btn btn-ghost btn-sm" onClick={() => setModal(d)}>Edit</button>
                      <button className="btn btn-danger btn-sm" onClick={() => setConfirm(d.id)}>Del</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <DeviceForm
          initial={modal === 'new' ? {} : modal}
          onSave={handleSave}
          onClose={() => setModal(null)}
          clients={clients}
        />
      )}
      {confirm && (
        <ConfirmModal
          message="Delete this device? All associated tickets will be affected."
          onConfirm={() => handleDelete(confirm)}
          onClose={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
